import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { api } from "../api/api";
import Kalender from "../components/Kalender";

// Lokales Kalenderdatum als "YYYY-MM-DD" – NICHT toISOString() verwenden,
// das konvertiert nach UTC und verschiebt das Datum in Deutschland um einen Tag.
function toLocalISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// ── Meine Besichtigungen ──────────────────────────────────
function MeineBesichtigungen({ besichtigungen }) {
  return (
    <div style={{ marginTop: "1.5rem" }}>
      {besichtigungen.length === 0 ? (
        <div className="card" style={{ textAlign: "center", color: "#888" }}>
          Du hast noch keine Besichtigungen angefragt.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {besichtigungen.map(b => (
            <div key={b.besichtigungId} className="card" style={{
              border: `1.5px solid ${b.status === "Bestätigt" ? "#a5d6a7" : b.status === "Abgelehnt" ? "#ffcdd2" : "#ffe0b2"}`
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <strong style={{ fontSize: "1.05rem" }}>
                    📅 {new Date(b.datum).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" })}
                  </strong>
                  <span style={{ marginLeft: "0.75rem", color: "#666" }}>um {b.uhrzeit} Uhr</span>
                </div>
                <span className="badge" style={{
                  background: b.status === "Bestätigt" ? "#e8f5e9" : b.status === "Abgelehnt" ? "#ffebee" : "#fff3e0",
                  color:      b.status === "Bestätigt" ? "#2e7d32" : b.status === "Abgelehnt" ? "#c62828" : "#e65100",
                  padding: "0.3rem 0.8rem"
                }}>
                  {b.status === "Bestätigt" ? "✓ Bestätigt" : b.status === "Abgelehnt" ? "✗ Abgelehnt" : "⏳ Ausstehend"}
                </span>
              </div>
              <p style={{ color: "#666", fontSize: "0.9rem", marginTop: "0.4rem" }}>
                👥 {b.personenanzahl} {b.personenanzahl === 1 ? "Person" : "Personen"}
                {b.nachricht && ` · "${b.nachricht}"`}
              </p>
              {b.adminNachricht && (
                <div style={{ marginTop: "0.5rem", padding: "0.5rem 0.75rem", background: "#f5f5f5", borderRadius: "8px", fontSize: "0.9rem" }}>
                  💬 <strong>Nachricht vom Hof:</strong> {b.adminNachricht}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Liefertermin-Berechnung ───────────────────────────────
function berechneLiefertermine(abo, vonStr, bisStr, ausnahmen) {
  const termine = [];
  if (!abo.istAktiv || abo.intervall === "Einzelbestellung") return termine;
  const ankerStr = abo.ankerDatum;
  if (!ankerStr) return termine;

  const anker = new Date(ankerStr + "T00:00:00");
  const von   = new Date(vonStr   + "T00:00:00");
  const bis   = new Date(bisStr   + "T00:00:00");

  const getAusnahme = (ds) => ausnahmen.find(a => a.bestellungId === abo.aboId && a.datum === ds);
  const isPause = (d) => {
    if (!abo.pauseVon || !abo.pauseBis) return false;
    return d >= new Date(abo.pauseVon + "T00:00:00") && d <= new Date(abo.pauseBis + "T00:00:00");
  };

  const pushTermin = (ds) => {
    const ausnahme = getAusnahme(ds);
    const verschoben  = !!ausnahme?.ersatzDatum;
    const ausgesetzt  = !!ausnahme && !verschoben && ausnahme.ersatzMenge == null;
    termine.push({
      datum: ds,
      ausgesetzt,
      verschoben,
      ersatzDatum: ausnahme?.ersatzDatum ?? null,
      ersatzMenge: ausnahme?.ersatzMenge ?? null,
    });
  };

  if (abo.intervall === "Monatlich") {
    let d = new Date(von.getFullYear(), von.getMonth(), anker.getDate());
    if (d < von) d.setMonth(d.getMonth() + 1);
    while (d <= bis) {
      if (!isPause(d)) { const ds = toLocalISO(d); pushTermin(ds); }
      d.setMonth(d.getMonth() + 1);
    }
  } else {
    const schritt = abo.intervall === "2-wöchentlich" ? 14 : 7;
    let d = new Date(anker);
    while (d < von) d.setDate(d.getDate() + schritt);
    const prev = new Date(d); prev.setDate(prev.getDate() - schritt);
    if (prev >= von) d = prev;
    while (d <= bis) {
      if (d >= von && !isPause(d)) { const ds = toLocalISO(d); pushTermin(ds); }
      d.setDate(d.getDate() + schritt);
    }
  }
  return termine;
}

const fmt = (ds) => new Date(ds + "T00:00:00").toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });

// ── Lieferkalender ────────────────────────────────────────
const ABO_FARBEN = ["#2e7d32","#1565c0","#e65100","#6a1b9a","#00838f","#c62828"];

// Fasst Lieferungen mit gleicher GruppeId (mehrere Produkte, gemeinsam angelegt)
// zu einem einzigen Eintrag zusammen, damit sie nicht wie getrennte Bestellungen wirken.
function gruppiereFuerAnzeige(lieferungen) {
  const gruppen = new Map();
  const result = [];
  for (const l of lieferungen) {
    const gid = l.abo.gruppeId;
    const normal = !l.ausgesetzt && !l.istErsatz && !l.verschoben;
    if (gid && normal && gruppen.has(gid)) {
      gruppen.get(gid).produktNamen.push(l.abo.produktName);
      continue;
    }
    const eintrag = { ...l, produktNamen: [l.abo.produktName] };
    result.push(eintrag);
    if (gid && normal) gruppen.set(gid, eintrag);
  }
  return result;
}

function Lieferkalender({ user, abos, betriebsurlaub }) {
  const heute = new Date(); heute.setHours(0,0,0,0);
  const [monat, setMonat]         = useState({ year: heute.getFullYear(), month: heute.getMonth() });
  const [ausnahmen, setAusnahmen] = useState([]);
  const [selected, setSelected]   = useState(null);
  const [loading, setLoading]     = useState(false);
  const [verschiebenFor, setVerschiebenFor] = useState(null); // { bestellungId, datum }
  const [verschiebenDatum, setVerschiebenDatum] = useState("");
  const [mengeFor, setMengeFor]   = useState(null); // { bestellungId, datum }
  const [neueMenge, setNeueMenge] = useState("");
  const [ausnahmeFehler, setAusnahmeFehler] = useState(null);

  const aktiveAbos = abos.filter(a => a.istAktiv && a.intervall !== "Einzelbestellung");

  useEffect(() => {
    if (!user) return;
    api.abos.getAusnahmen(user.userId).then(setAusnahmen).catch(() => {});
  }, [user]);

  const { startTag, endTag, tage } = useMemo(() => {
    const erster  = new Date(monat.year, monat.month, 1);
    const letzter = new Date(monat.year, monat.month + 1, 0);
    const start   = new Date(erster);
    const wt = start.getDay() || 7; start.setDate(start.getDate() - (wt - 1));
    const end = new Date(letzter);
    const wtE = end.getDay() || 7; end.setDate(end.getDate() + (7 - wtE));
    const days = [];
    let d = new Date(start);
    while (d <= end) { days.push(new Date(d)); d.setDate(d.getDate() + 1); }
    return { startTag: start, endTag: end, tage: days };
  }, [monat]);

  const vonStr = toLocalISO(startTag);
  const bisStr = toLocalISO(endTag);

  const lieferMap = useMemo(() => {
    const map = {};
    aktiveAbos.forEach((abo, idx) => {
      const farbe = ABO_FARBEN[idx % ABO_FARBEN.length];
      berechneLiefertermine(abo, vonStr, bisStr, ausnahmen).forEach(t => {
        if (!map[t.datum]) map[t.datum] = [];
        map[t.datum].push({ ...t, abo, farbe });
      });
      // Ersatzlieferungen (verschoben auf neues Datum) anzeigen
      ausnahmen
        .filter(a => a.bestellungId === abo.aboId && a.ersatzDatum)
        .filter(a => a.ersatzDatum >= vonStr && a.ersatzDatum <= bisStr)
        .forEach(a => {
          if (!map[a.ersatzDatum]) map[a.ersatzDatum] = [];
          map[a.ersatzDatum].push({
            datum: a.ersatzDatum, ausgesetzt: false, verschoben: false,
            istErsatz: true, originalDatum: a.datum, ersatzMenge: a.ersatzMenge,
            abo, farbe,
          });
        });
    });
    return map;
  }, [aktiveAbos, vonStr, bisStr, ausnahmen]);

  const inUrlaub = (ds) => betriebsurlaub.some(u => ds >= u.urlaubVon?.slice(0,10) && ds <= u.urlaubBis?.slice(0,10));
  const heuteStr = toLocalISO(heute);

  const reloadSelected = (datum, newAusnahmen) => {
    const lieferungen = (() => {
      const map = {};
      aktiveAbos.forEach((abo, idx) => {
        const farbe = ABO_FARBEN[idx % ABO_FARBEN.length];
        berechneLiefertermine(abo, datum, datum, newAusnahmen).forEach(t => {
          if (!map[t.datum]) map[t.datum] = [];
          map[t.datum].push({ ...t, abo, farbe });
        });
        newAusnahmen
          .filter(a => a.bestellungId === abo.aboId && a.ersatzDatum === datum)
          .forEach(a => {
            if (!map[datum]) map[datum] = [];
            map[datum].push({
              datum, ausgesetzt: false, verschoben: false,
              istErsatz: true, originalDatum: a.datum, ersatzMenge: a.ersatzMenge,
              abo, farbe: ABO_FARBEN[idx % ABO_FARBEN.length],
            });
          });
      });
      return map[datum] || [];
    })();
    setSelected(s => s ? { ...s, lieferungen } : s);
  };

  const removeAusnahmeLocal = async (bestellungId, datum) => {
    setLoading(true); setAusnahmeFehler(null);
    try {
      await api.abos.removeAusnahme({ bestellungId, datum });
      const next = ausnahmen.filter(a => !(a.bestellungId === bestellungId && a.datum === datum));
      setAusnahmen(next);
      reloadSelected(selected?.datum, next);
    } catch (e) { setAusnahmeFehler(e.message ?? "Fehler beim Entfernen."); }
    setLoading(false);
  };

  const toggleAusnahme = async (bestellungId, datum) => {
    const exists = ausnahmen.some(a => a.bestellungId === bestellungId && a.datum === datum && !a.ersatzDatum && a.ersatzMenge == null);
    if (exists) {
      await removeAusnahmeLocal(bestellungId, datum);
    } else {
      setLoading(true); setAusnahmeFehler(null);
      try {
        await api.abos.addAusnahme({ bestellungId, datum });
        const next = [...ausnahmen.filter(a => !(a.bestellungId === bestellungId && a.datum === datum)), { bestellungId, datum, ersatzDatum: null, ersatzMenge: null }];
        setAusnahmen(next);
        reloadSelected(selected?.datum, next);
      } catch (e) { setAusnahmeFehler(e.message ?? "Fehler beim Überspringen."); }
      setLoading(false);
    }
  };

  const verschiebenSpeichern = async (bestellungId, datum) => {
    if (!verschiebenDatum || verschiebenDatum === datum) return;
    setLoading(true); setAusnahmeFehler(null);
    try {
      const existing = ausnahmen.find(a => a.bestellungId === bestellungId && a.datum === datum);
      await api.abos.addAusnahme({
        bestellungId, datum,
        ersatzDatum: verschiebenDatum,
        ersatzMenge: existing?.ersatzMenge ?? null,
      });
      const next = [
        ...ausnahmen.filter(a => !(a.bestellungId === bestellungId && a.datum === datum)),
        { bestellungId, datum, ersatzDatum: verschiebenDatum, ersatzMenge: existing?.ersatzMenge ?? null },
      ];
      setAusnahmen(next);
      reloadSelected(selected?.datum, next);
      setVerschiebenFor(null);
      setVerschiebenDatum("");
    } catch (e) { setAusnahmeFehler(e.message ?? "Fehler beim Verschieben."); }
    setLoading(false);
  };

  const mengeSpeichern = async (bestellungId, datum) => {
    const menge = parseFloat(neueMenge);
    if (!neueMenge || isNaN(menge) || menge <= 0) return;
    setLoading(true); setAusnahmeFehler(null);
    try {
      const existing = ausnahmen.find(a => a.bestellungId === bestellungId && a.datum === datum);
      await api.abos.addAusnahme({
        bestellungId, datum,
        ersatzDatum: existing?.ersatzDatum ?? null,
        ersatzMenge: menge,
      });
      const next = [
        ...ausnahmen.filter(a => !(a.bestellungId === bestellungId && a.datum === datum)),
        { bestellungId, datum, ersatzDatum: existing?.ersatzDatum ?? null, ersatzMenge: menge },
      ];
      setAusnahmen(next);
      reloadSelected(selected?.datum, next);
      setMengeFor(null);
      setNeueMenge("");
    } catch (e) { setAusnahmeFehler(e.message ?? "Fehler beim Speichern der Menge."); }
    setLoading(false);
  };

  if (aktiveAbos.length === 0)
    return <div className="card" style={{ marginTop: "1.5rem", color: "#888", textAlign: "center" }}>Keine aktiven Abos vorhanden.</div>;

  return (
    <div style={{ marginTop: "1.5rem" }}>
      {ausnahmeFehler && (
        <div style={{ background: "#ffebee", color: "#c62828", border: "1px solid #ffcdd2", borderRadius: 8, padding: "0.6rem 0.9rem", marginBottom: "0.75rem", fontSize: "0.85rem" }}>
          {ausnahmeFehler}
        </div>
      )}
      {/* Legende */}
      <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginBottom: "0.75rem" }}>
        {aktiveAbos.map((a, i) => (
          <span key={a.aboId} style={{ fontSize: "0.82rem", display: "flex", alignItems: "center", gap: "0.3rem" }}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", background: ABO_FARBEN[i % ABO_FARBEN.length], display: "inline-block" }} />
            {a.produktName}
          </span>
        ))}
      </div>

      {/* Monat-Navigation */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.75rem" }}>
        <button className="btn btn-secondary" style={{ padding: "0.3rem 0.9rem", fontWeight: 700 }}
          onClick={() => setMonat(m => { const d = new Date(m.year, m.month-1); return { year: d.getFullYear(), month: d.getMonth() }; })}>‹</button>
        <strong style={{ color: "#2e7d32", fontSize: "1.05rem" }}>
          {new Date(monat.year, monat.month).toLocaleDateString("de-DE", { month: "long", year: "numeric" })}
        </strong>
        <button className="btn btn-secondary" style={{ padding: "0.3rem 0.9rem", fontWeight: 700 }}
          onClick={() => setMonat(m => { const d = new Date(m.year, m.month+1); return { year: d.getFullYear(), month: d.getMonth() }; })}>›</button>
      </div>

      {/* Kalender-Grid */}
      <div style={{ borderRadius: 12, border: "1.5px solid #e0e0e0", overflow: "hidden", background: "white" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", background: "#f5f5f5", borderBottom: "1px solid #e0e0e0" }}>
          {["Mo","Di","Mi","Do","Fr","Sa","So"].map(t => (
            <div key={t} style={{ padding: "0.4rem", textAlign: "center", fontSize: "0.82rem", fontWeight: 600, color: "#666" }}>{t}</div>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
          {tage.map(tag => {
            const ds          = toLocalISO(tag);
            const dieserMonat = tag.getMonth() === monat.month;
            const istHeute    = ds === heuteStr;
            const urlaub      = inUrlaub(ds);
            const lieferungen = lieferMap[ds] || [];
            const isSelected  = selected?.datum === ds;

            return (
              <div key={ds}
                onClick={() => lieferungen.length > 0 && setSelected(isSelected ? null : { datum: ds, lieferungen })}
                style={{
                  padding: "0.35rem 0.4rem", minHeight: "3.8rem",
                  border: "1px solid #f0f0f0",
                  background: isSelected ? "#e8f5e9" : istHeute ? "#f1f8e9" : urlaub ? "#fafafa" : "white",
                  opacity: dieserMonat ? 1 : 0.35,
                  cursor: lieferungen.length > 0 ? "pointer" : "default",
                }}>
                <span style={{ fontSize: "0.82rem", fontWeight: istHeute ? 700 : 400, color: istHeute ? "#2e7d32" : "#444" }}>
                  {tag.getDate()}
                </span>
                {urlaub && <div style={{ fontSize: "0.62rem", color: "#bbb" }}>Urlaub</div>}
                {gruppiereFuerAnzeige(lieferungen).map((l, i) => {
                  const bg    = l.ausgesetzt ? "#ffebee" : l.verschoben ? "#fff8e1" : l.istErsatz ? "#e8f5e9" : l.farbe + "20";
                  const color = l.ausgesetzt ? "#c62828" : l.verschoben ? "#f57f17" : l.istErsatz ? "#2e7d32" : l.farbe;
                  const icon  = l.ausgesetzt ? "✗" : l.verschoben ? "→" : l.istErsatz ? "↩" : "🚚";
                  return (
                    <div key={i} style={{
                      marginTop: "0.15rem", padding: "0.1rem 0.25rem", borderRadius: 4,
                      fontSize: "0.65rem", lineHeight: 1.2, background: bg, color,
                      border: `1px solid ${l.ausgesetzt ? "#ffcdd2" : l.verschoben ? "#ffe082" : l.istErsatz ? "#a5d6a7" : l.farbe + "50"}`,
                      textDecoration: l.ausgesetzt || l.verschoben ? "line-through" : "none",
                      overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis",
                    }}>
                      {icon} {l.produktNamen.join(" + ")}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {/* Detail-Panel bei Klick */}
      {selected && (
        <div className="card" style={{ marginTop: "0.75rem", border: "1.5px solid #a5d6a7" }}>
          <strong style={{ color: "#2e7d32" }}>
            {new Date(selected.datum + "T00:00:00").toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long" })}
          </strong>

          {selected.lieferungen.map((l, i) => {
            const bestellungId = l.abo.aboId;
            const datum        = l.istErsatz ? l.originalDatum : selected.datum;
            const isVerschiebenOpen = verschiebenFor?.bestellungId === bestellungId && verschiebenFor?.datum === datum;
            const isMengeOpen       = mengeFor?.bestellungId === bestellungId && mengeFor?.datum === datum;
            const anzeigemenge      = l.ersatzMenge ?? l.abo.menge;

            return (
              <div key={i} style={{ marginTop: "0.75rem", paddingTop: i > 0 ? "0.75rem" : 0, borderTop: i > 0 ? "1px solid #f0f0f0" : "none" }}>

                {/* Zeile: Info + Buttons */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem", flexWrap: "wrap" }}>
                  <div>
                    <strong style={{ textDecoration: l.ausgesetzt || l.verschoben ? "line-through" : "none" }}>
                      {l.abo.produktName}
                    </strong>
                    <span style={{ color: "#888", fontSize: "0.85rem", marginLeft: "0.5rem" }}>
                      {anzeigemenge} {l.abo.einheit}
                      {l.ersatzMenge && <span style={{ color: "#1565c0", fontWeight: 600 }}> (angepasst)</span>}
                      {" · "}{l.abo.intervall}
                    </span>
                    {l.ausgesetzt && <span style={{ marginLeft: "0.5rem", color: "#c62828", fontSize: "0.82rem" }}>✗ Übersprungen</span>}
                    {l.verschoben && <span style={{ marginLeft: "0.5rem", color: "#f57f17", fontSize: "0.82rem" }}>→ verschoben auf {fmt(l.ersatzDatum)}</span>}
                    {l.istErsatz  && <span style={{ marginLeft: "0.5rem", color: "#2e7d32", fontSize: "0.82rem" }}>↩ verschoben von {fmt(l.originalDatum)}</span>}
                  </div>

                  <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                    {/* Rückgängig bei Ersatz/Verschoben/Ausgesetzt */}
                    {(l.ausgesetzt || l.verschoben || l.istErsatz || l.ersatzMenge != null) && (
                      <button disabled={loading}
                        onClick={() => removeAusnahmeLocal(bestellungId, datum)}
                        style={{ padding: "0.3rem 0.7rem", fontSize: "0.8rem", borderRadius: 6, border: "none", cursor: "pointer", background: "#e8f5e9", color: "#2e7d32" }}>
                        {loading ? "…" : "↩ Wiederherstellen"}
                      </button>
                    )}

                    {/* Buttons nur für normale Lieferungen */}
                    {!l.ausgesetzt && !l.verschoben && !l.istErsatz && (
                      <>
                        <button disabled={loading}
                          onClick={() => toggleAusnahme(bestellungId, selected.datum)}
                          style={{ padding: "0.3rem 0.7rem", fontSize: "0.8rem", borderRadius: 6, border: "none", cursor: "pointer", background: "#ffebee", color: "#c62828" }}>
                          {loading ? "…" : "✗ Überspringen"}
                        </button>
                        <button disabled={loading}
                          onClick={() => { setVerschiebenFor({ bestellungId, datum: selected.datum }); setVerschiebenDatum(""); setMengeFor(null); }}
                          style={{ padding: "0.3rem 0.7rem", fontSize: "0.8rem", borderRadius: 6, border: "none", cursor: "pointer", background: "#fff8e1", color: "#f57f17" }}>
                          → Verschieben
                        </button>
                        <button disabled={loading}
                          onClick={() => { setMengeFor({ bestellungId, datum: selected.datum }); setNeueMenge(String(l.ersatzMenge ?? l.abo.menge)); setVerschiebenFor(null); }}
                          style={{ padding: "0.3rem 0.7rem", fontSize: "0.8rem", borderRadius: 6, border: "none", cursor: "pointer", background: "#e3f2fd", color: "#1565c0" }}>
                          ✏ Menge
                        </button>
                      </>
                    )}

                    {/* Menge-Button auch bei Ersatz-Lieferung */}
                    {l.istErsatz && (
                      <button disabled={loading}
                        onClick={() => { setMengeFor({ bestellungId, datum }); setNeueMenge(String(l.ersatzMenge ?? l.abo.menge)); setVerschiebenFor(null); }}
                        style={{ padding: "0.3rem 0.7rem", fontSize: "0.8rem", borderRadius: 6, border: "none", cursor: "pointer", background: "#e3f2fd", color: "#1565c0" }}>
                        ✏ Menge
                      </button>
                    )}
                  </div>
                </div>

                {/* Inline-Formular: Verschieben */}
                {isVerschiebenOpen && (
                  <div style={{ marginTop: "0.6rem", padding: "0.75rem", background: "#fffde7", borderRadius: 8, border: "1px solid #ffe082" }}>
                    <div style={{ fontSize: "0.85rem", fontWeight: 600, marginBottom: "0.4rem", color: "#f57f17" }}>
                      Lieferung verschieben auf:
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                      <input type="date" value={verschiebenDatum}
                        min={heuteStr}
                        onChange={e => setVerschiebenDatum(e.target.value)}
                        style={{ padding: "0.3rem 0.5rem", borderRadius: 6, border: "1px solid #ffe082", fontSize: "0.85rem" }} />
                      <button disabled={loading || !verschiebenDatum}
                        onClick={() => verschiebenSpeichern(bestellungId, datum)}
                        style={{ padding: "0.3rem 0.8rem", fontSize: "0.85rem", borderRadius: 6, border: "none", cursor: verschiebenDatum ? "pointer" : "not-allowed", background: "#f57f17", color: "white" }}>
                        {loading ? "…" : "Speichern"}
                      </button>
                      <button onClick={() => { setVerschiebenFor(null); setVerschiebenDatum(""); }}
                        style={{ padding: "0.3rem 0.7rem", fontSize: "0.85rem", borderRadius: 6, border: "1px solid #ddd", cursor: "pointer", background: "white" }}>
                        Abbrechen
                      </button>
                    </div>
                    {ausnahmeFehler && (
                      <div style={{ marginTop: "0.5rem", color: "#c62828", fontSize: "0.8rem" }}>{ausnahmeFehler}</div>
                    )}
                  </div>
                )}

                {/* Inline-Formular: Menge anpassen */}
                {isMengeOpen && (
                  <div style={{ marginTop: "0.6rem", padding: "0.75rem", background: "#e3f2fd", borderRadius: 8, border: "1px solid #90caf9" }}>
                    <div style={{ fontSize: "0.85rem", fontWeight: 600, marginBottom: "0.4rem", color: "#1565c0" }}>
                      Menge für diese Lieferung anpassen:
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                      <input type="number" value={neueMenge} min="0.1" step="0.1"
                        onChange={e => setNeueMenge(e.target.value)}
                        style={{ padding: "0.3rem 0.5rem", borderRadius: 6, border: "1px solid #90caf9", fontSize: "0.85rem", width: "6rem" }} />
                      <span style={{ fontSize: "0.85rem", color: "#666" }}>{l.abo.einheit}</span>
                      <button disabled={loading || !neueMenge}
                        onClick={() => mengeSpeichern(bestellungId, datum)}
                        style={{ padding: "0.3rem 0.8rem", fontSize: "0.85rem", borderRadius: 6, border: "none", cursor: neueMenge ? "pointer" : "not-allowed", background: "#1565c0", color: "white" }}>
                        {loading ? "…" : "Speichern"}
                      </button>
                      <button onClick={() => { setMengeFor(null); setNeueMenge(""); }}
                        style={{ padding: "0.3rem 0.7rem", fontSize: "0.85rem", borderRadius: 6, border: "1px solid #ddd", cursor: "pointer", background: "white" }}>
                        Abbrechen
                      </button>
                    </div>
                    {ausnahmeFehler && (
                      <div style={{ marginTop: "0.5rem", color: "#c62828", fontSize: "0.8rem" }}>{ausnahmeFehler}</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p style={{ fontSize: "0.8rem", color: "#aaa", marginTop: "0.5rem" }}>
        Tipp: Klicke auf einen Liefertag um Details zu sehen. Dauerhafte Änderungen unter "Abo ändern".
      </p>
    </div>
  );
}

// ── Nachricht schreiben ───────────────────────────────────
function NachrichtSchreiben({ user }) {
  const [form, setForm] = useState({ betreff: "", text: "" });
  const [success, setSuccess] = useState(false);
  const [error, setError]     = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await api.nachrichten.create({
        name:    user.name || user.email,
        email:   user.email,
        betreff: form.betreff,
        text:    form.text
      });
      setSuccess(true);
      setForm({ betreff: "", text: "" });
    } catch {
      setError("Nachricht konnte nicht gesendet werden. Bitte versuche es erneut.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ marginTop: "1.5rem", maxWidth: 550 }}>
      <div className="card" style={{ border: "1.5px solid #e8f5e9" }}>
        <h3 style={{ color: "#2e7d32", marginBottom: "1rem" }}>✉️ Nachricht an den Hof</h3>
        {success && <div className="alert alert-success">✓ Nachricht erfolgreich gesendet!</div>}
        {error   && <div className="alert alert-error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Betreff</label>
            <input type="text" value={form.betreff} placeholder="Worum geht es?"
              onChange={e => setForm({ ...form, betreff: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Nachricht *</label>
            <textarea value={form.text} required rows={5} placeholder="Deine Nachricht..."
              onChange={e => setForm({ ...form, text: e.target.value })}
              style={{ resize: "vertical" }} />
          </div>
          <button className="btn" type="submit" disabled={loading}
            style={{ background: "#2e7d32", color: "white", width: "100%" }}>
            {loading ? "Wird gesendet..." : "✉️ Nachricht senden"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Passwort Ändern ───────────────────────────────────────
function PasswortAendern({ user }) {
  const [form, setForm] = useState({ altesPasswort: "", neuesPasswort: "", neuesPasswort2: "" });
  const [success, setSuccess] = useState(false);
  const [error, setError]     = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (form.neuesPasswort !== form.neuesPasswort2) {
      setError("Die neuen Passwörter stimmen nicht überein.");
      return;
    }
    if (form.neuesPasswort.length < 6) {
      setError("Das neue Passwort muss mindestens 6 Zeichen lang sein.");
      return;
    }
    setLoading(true);
    try {
      await api.auth.changePassword({
        userId: user.userId,
        altesPasswort: form.altesPasswort,
        neuesPasswort: form.neuesPasswort
      });
      setSuccess(true);
      setForm({ altesPasswort: "", neuesPasswort: "", neuesPasswort2: "" });
    } catch (err) {
      setError(err.message || "Fehler beim Ändern des Passworts.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ marginTop: "1.5rem", maxWidth: 450 }}>
      <div className="card" style={{ border: "1.5px solid #e3f2fd" }}>
        <h3 style={{ color: "#1565c0", marginBottom: "1rem" }}>🔒 Passwort ändern</h3>
        {success && <div className="alert alert-success">✓ Passwort erfolgreich geändert!</div>}
        {error   && <div className="alert alert-error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Aktuelles Passwort *</label>
            <input type="password" value={form.altesPasswort} required
              onChange={e => setForm({ ...form, altesPasswort: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Neues Passwort *</label>
            <input type="password" value={form.neuesPasswort} required
              onChange={e => setForm({ ...form, neuesPasswort: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Neues Passwort wiederholen *</label>
            <input type="password" value={form.neuesPasswort2} required
              onChange={e => setForm({ ...form, neuesPasswort2: e.target.value })} />
          </div>
          <button className="btn" type="submit" disabled={loading}
            style={{ background: "#1565c0", color: "white", width: "100%" }}>
            {loading ? "Wird gespeichert..." : "🔒 Passwort ändern"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Besichtigung Formular ─────────────────────────────────
function BesichtigungForm({ user, besichtigungen, betriebsurlaub = [], abos = [], onSuccess }) {
  const [selectedDatum, setSelectedDatum] = useState("");
  const [form, setForm] = useState({ uhrzeit: "10:00", personenanzahl: 1, nachricht: "" });
  const [success, setSuccess] = useState(false);
  const [error, setError]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [preis,   setPreis]   = useState(null);

  const hatAktivesAbo = abos.some(a => a.istAktiv);

  useEffect(() => {
    api.einstellungen.get("BesichtigungPreis").then(v => setPreis(parseFloat(v))).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDatum) { setError("Bitte wähle ein Datum im Kalender."); return; }
    setLoading(true);
    setError(null);
    try {
      await api.besichtigungen.create({
        userId: user.userId,
        datum: selectedDatum,
        ...form
      });
      setSuccess(true);
      setSelectedDatum("");
      onSuccess();
    } catch (err) {
      setError(err.message || "Fehler beim Anmelden.");
    } finally {
      setLoading(false);
    }
  };

  const gesamtPreis = preis != null && !hatAktivesAbo ? preis.toFixed(2) : null;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem", marginTop: "1.5rem" }}>
      {/* ── Kalender ── */}
      <div>
        <p style={{ marginBottom: "0.75rem", color: "#555", fontSize: "0.95rem" }}>
          Wähle ein freies Datum:
        </p>
        <Kalender
          besichtigungen={besichtigungen}
          betriebsurlaub={betriebsurlaub}
          onDatumSelect={setSelectedDatum}
          selectedDatum={selectedDatum}
        />
      </div>

      {/* ── Formular ── */}
      <div className="card" style={{ border: "1.5px solid #a5d6a7" }}>
        <h3 style={{ color: "#2e7d32", marginBottom: "1rem" }}>🐄 Besichtigung anmelden</h3>

        {/* Preis-Hinweis */}
        {hatAktivesAbo ? (
          <div style={{ background: "#e8f5e9", border: "1px solid #a5d6a7", borderRadius: "8px", padding: "0.75rem 1rem", marginBottom: "1rem", fontSize: "0.9rem", color: "#2e7d32" }}>
            🎉 <strong>Kostenlos!</strong> Als Abo-Kunde ist die Besichtigung für dich gratis.
          </div>
        ) : preis != null && preis > 0 ? (
          <div style={{ background: "#fff3e0", border: "1px solid #ffcc80", borderRadius: "8px", padding: "0.75rem 1rem", marginBottom: "1rem", fontSize: "0.9rem", color: "#e65100" }}>
            💰 Eintritt: <strong>{preis.toFixed(2)} €</strong>
            <div style={{ fontSize: "0.8rem", color: "#888", marginTop: "0.25rem" }}>Mit einem Abo wäre die Besichtigung kostenlos.</div>
          </div>
        ) : null}

        {success && <div className="alert alert-success">✓ Anfrage gesendet! Der Admin wird dich bestätigen.</div>}
        {error   && <div className="alert alert-error">{error}</div>}

        {selectedDatum && (
          <div className="alert alert-success" style={{ marginBottom: "1rem" }}>
            📅 Ausgewählt: <strong>{new Date(selectedDatum).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" })}</strong>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Uhrzeit *</label>
            <select value={form.uhrzeit} onChange={e => setForm({ ...form, uhrzeit: e.target.value })}>
              {["08:00","09:00","10:00","11:00","12:00","13:00","14:00","15:00","16:00"].map(u => (
                <option key={u}>{u}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Personenanzahl *</label>
            <input type="number" min="1" max="20" value={form.personenanzahl}
              onChange={e => setForm({ ...form, personenanzahl: parseInt(e.target.value) })} required />
          </div>
          <div className="form-group">
            <label>Nachricht / Anmerkung</label>
            <textarea rows={3} value={form.nachricht}
              onChange={e => setForm({ ...form, nachricht: e.target.value })}
              placeholder="z.B. Wir kommen mit Kindern, haben Fragen zu..." />
          </div>
          <button className="btn btn-success" type="submit" disabled={loading || !selectedDatum}
            style={{ width: "100%" }}>
            {loading ? "Wird gesendet..." : hatAktivesAbo ? "📅 Kostenlos anfragen" : gesamtPreis ? `📅 Anfragen (${gesamtPreis} €)` : "📅 Besichtigung anfragen"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Abo Beenden ───────────────────────────────────────────
function BeendenForm({ abos, onBeendet }) {
  const [selectedAboId, setSelectedAboId] = useState("");
  const [confirm, setConfirm]   = useState(false);
  const [success, setSuccess]   = useState(false);
  const [error, setError]       = useState(null);
  const [loading, setLoading]   = useState(false);

  const aktiveAbos = abos.filter(a => a.istAktiv);

  const handleBeenden = async () => {
    setLoading(true);
    setError(null);
    try {
      await api.abos.beenden(selectedAboId);
      setSuccess(true);
      setConfirm(false);
      onBeendet && onBeendet();
    } catch { setError("Fehler beim Beenden."); }
    finally { setLoading(false); }
  };

  return (
    <div className="card" style={{ border: "1.5px solid #ffcdd2" }}>
      <h3 style={{ color: "#c62828", marginBottom: "1rem" }}>❌ Abo beenden</h3>
      {success && <div className="alert alert-success">✓ Abo wurde beendet.</div>}
      {error   && <div className="alert alert-error">{error}</div>}
      {aktiveAbos.length === 0
        ? <p style={{ color: "#888" }}>Du hast keine aktiven Abos.</p>
        : (
          <>
            <div className="form-group">
              <label>Abo auswählen *</label>
              <select value={selectedAboId} onChange={e => { setSelectedAboId(e.target.value); setConfirm(false); }}>
                <option value="">– wählen –</option>
                {aktiveAbos.map(a => (
                  <option key={a.aboId} value={a.aboId}>
                    {a.produktName} – {a.intervall} (#{a.aboId})
                  </option>
                ))}
              </select>
            </div>

            {selectedAboId && !confirm && (
              <button className="btn" onClick={() => setConfirm(true)}
                style={{ background: "#c62828", color: "white" }}>
                ❌ Abo beenden
              </button>
            )}

            {confirm && (
              <div style={{ background: "#ffebee", padding: "1rem", borderRadius: "8px", marginTop: "0.5rem" }}>
                <p style={{ color: "#c62828", fontWeight: 600, marginBottom: "0.75rem" }}>
                  ⚠️ Bist du sicher? Das Abo wird dauerhaft beendet.
                </p>
                <div style={{ display: "flex", gap: "0.75rem" }}>
                  <button className="btn" onClick={handleBeenden} disabled={loading}
                    style={{ background: "#c62828", color: "white" }}>
                    {loading ? "..." : "Ja, beenden"}
                  </button>
                  <button className="btn btn-secondary" onClick={() => setConfirm(false)}>
                    Abbrechen
                  </button>
                </div>
              </div>
            )}
          </>
        )
      }
    </div>
  );
}

// ── Datum-Hilfsfunktionen ──────────────────────────────────
function heute() { return toLocalISO(new Date()); }

function isInUrlaub(datum, urlaube = []) {
  if (!datum) return false;
  return urlaube.some(u => datum >= u.urlaubVon.slice(0,10) && datum <= u.urlaubBis.slice(0,10));
}

function naechsterErlaubterTag(urlaube = []) {
  const d = new Date();
  for (let i = 0; i < 365; i++) {
    const str = toLocalISO(d);
    if (!isInUrlaub(str, urlaube)) return str;
    d.setDate(d.getDate() + 1);
  }
  return heute();
}

// ── Abo Pausieren ─────────────────────────────────────────
function PausierenForm({ abos, betriebsurlaub = [] }) {
  const [form, setForm] = useState({ aboId: "", pauseVon: "", pauseBis: "" });
  const [success, setSuccess] = useState(false);
  const [error, setError]     = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (form.pauseVon >= form.pauseBis) { setError("'Pause bis' muss nach 'Pause von' liegen."); return; }
    if (isInUrlaub(form.pauseVon, betriebsurlaub)) { setError("Der Starttermin liegt im Betriebsurlaub."); return; }
    if (isInUrlaub(form.pauseBis, betriebsurlaub)) { setError("Der Endtermin liegt im Betriebsurlaub."); return; }
    setLoading(true);
    try {
      await api.abos.pausieren(form);
      setSuccess(true);
      setForm({ aboId: "", pauseVon: "", pauseBis: "" });
    } catch { setError("Fehler beim Pausieren."); }
    finally { setLoading(false); }
  };

  return (
    <div className="card" style={{ border: "1.5px solid #fff3e0" }}>
      <h3 style={{ color: "#e65100", marginBottom: "1rem" }}>⏸️ Abo pausieren</h3>
      {success && <div className="alert alert-success">✓ Abo wurde pausiert!</div>}
      {error   && <div className="alert alert-error">{error}</div>}
      {abos.length === 0
        ? <p style={{ color: "#888" }}>Du hast noch keine aktiven Abos.</p>
        : (
          <form onSubmit={handleSubmit}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 1rem" }}>
              <div className="form-group">
                <label>Abo *</label>
                <select value={form.aboId} onChange={e => setForm({ ...form, aboId: e.target.value })} required>
                  <option value="">– wählen –</option>
                  {abos.map(a => (
                    <option key={a.aboId} value={a.aboId}>
                      {a.produktName} ({a.intervall})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Pause von *</label>
                <input type="date" value={form.pauseVon} min={heute()}
                  onChange={e => setForm({ ...form, pauseVon: e.target.value })} required />
                {isInUrlaub(form.pauseVon, betriebsurlaub) && (
                  <small style={{ color: "#c62828" }}>⚠️ Betriebsurlaub an diesem Tag</small>
                )}
              </div>
              <div className="form-group">
                <label>Pause bis *</label>
                <input type="date" value={form.pauseBis} min={form.pauseVon || heute()}
                  onChange={e => setForm({ ...form, pauseBis: e.target.value })} required />
                {isInUrlaub(form.pauseBis, betriebsurlaub) && (
                  <small style={{ color: "#c62828" }}>⚠️ Betriebsurlaub an diesem Tag</small>
                )}
              </div>
            </div>
            <button className="btn" type="submit" disabled={loading}
              style={{ background: "#e65100", color: "white" }}>
              {loading ? "..." : "⏸️ Jetzt pausieren"}
            </button>
          </form>
        )
      }
    </div>
  );
}

// ── Abo Ändern ────────────────────────────────────────────
function AendernForm({ abos, produkte }) {
  const [form, setForm] = useState({ aboId: "", produktId: 0, menge: 0, intervall: "" });
  const [success, setSuccess] = useState(false);
  const [error, setError]     = useState(null);
  const [loading, setLoading] = useState(false);

  const handleAboSelect = (aboId) => {
    const abo = abos.find(a => String(a.aboId) === aboId);
    if (abo) setForm({ aboId, produktId: abo.produktId, menge: abo.menge, intervall: abo.intervall ?? "" });
    else setForm({ aboId, produktId: 0, menge: 0, intervall: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await api.abos.aendern(form);
      setSuccess(true);
    } catch { setError("Fehler beim Ändern."); }
    finally { setLoading(false); }
  };

  return (
    <div className="card" style={{ border: "1.5px solid #e3f2fd" }}>
      <h3 style={{ color: "#1565c0", marginBottom: "1rem" }}>✏️ Abo ändern</h3>
      {success && <div className="alert alert-success">✓ Abo wurde geändert!</div>}
      {error   && <div className="alert alert-error">{error}</div>}
      {abos.length === 0
        ? <p style={{ color: "#888" }}>Du hast noch keine aktiven Abos.</p>
        : (
          <form onSubmit={handleSubmit}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 1rem" }}>
              <div className="form-group">
                <label>Abo *</label>
                <select value={form.aboId} onChange={e => handleAboSelect(e.target.value)} required>
                  <option value="">– wählen –</option>
                  {abos.map(a => (
                    <option key={a.aboId} value={a.aboId}>
                      {a.produktName} ({a.intervall})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Produkt ändern</label>
                <select value={form.produktId} onChange={e => setForm({ ...form, produktId: parseInt(e.target.value) })}>
                  <option value={0}>– unverändert –</option>
                  {produkte.map(p => (
                    <option key={p.produktId} value={p.produktId}>
                      {p.name} ({p.preis?.toFixed(2)} € / {p.einheit})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Neue Menge</label>
                <input type="number" min="1" value={form.menge || ""}
                  onChange={e => setForm({ ...form, menge: parseFloat(e.target.value) })}
                  placeholder="unverändert" />
              </div>
              <div className="form-group">
                <label>Neues Intervall</label>
                <select value={form.intervall} onChange={e => setForm({ ...form, intervall: e.target.value })}>
                  <option value="">– unverändert –</option>
                  <option>Einzelbestellung</option>
                  <option>Wöchentlich</option>
                  <option>2-wöchentlich</option>
                  <option>Monatlich</option>
                </select>
              </div>
            </div>
            <button className="btn" type="submit" disabled={loading || !form.aboId}
              style={{ background: "#1565c0", color: "white" }}>
              {loading ? "..." : "✏️ Änderung speichern"}
            </button>
          </form>
        )
      }
    </div>
  );
}

// ── Abo Formulare ─────────────────────────────────────────
function AboForms({ user, produkte, abos, betriebsurlaub = [] }) {
  const [tab, setTab] = useState("abo-neu");
  const FORM_LEER = { produktId: "", menge: 1, intervall: user?.rolle === "Firma" ? "Einzelbestellung" : "Wöchentlich", wunschStart: "", strasse: "", hausnummer: "", plz: "", ort: "", notiz: "", ablageort: "", rechnungsAdresseId: "" };
  const [form, setForm] = useState(FORM_LEER);
  const [savedAdressen, setSavedAdressen] = useState([]);

  useEffect(() => {
    if (user) api.adressen.getByUser(user.userId).then(setSavedAdressen).catch(() => {});
  }, [user]);
  const [success, setSuccess]             = useState(false);
  const [error, setError]                 = useState(null);
  const [gutscheinCode, setGutscheinCode] = useState("");
  const [gutschein, setGutschein]         = useState(null);
  const [gutscheinFehler, setGutscheinFehler] = useState(null);
  const [lieferpauschale, setLieferpauschale] = useState(1.60);
  const [dsgvoAkzeptiert, setDsgvoAkzeptiert] = useState(false);

  useEffect(() => {
    api.einstellungen.get("Lieferpauschale")
      .then(v => setLieferpauschale(parseFloat(v)))
      .catch(() => {});
  }, []);

  const pruefeGutschein = async () => {
    setGutscheinFehler(null);
    setGutschein(null);
    if (!gutscheinCode.trim()) return;
    try {
      const g = await api.gutscheine.pruefen(gutscheinCode.trim()).catch(() => null);
      if (!g?.istAktiv) { setGutscheinFehler("❌ Ungültiger oder inaktiver Code."); return; }
      if (g.gueltigBis && new Date(g.gueltigBis + "T23:59:59") < new Date()) { setGutscheinFehler("❌ Code ist abgelaufen."); return; }
      if (g.maxNutzungen && g.nutzungen >= g.maxNutzungen) { setGutscheinFehler("❌ Code wurde bereits zu oft verwendet."); return; }
      setGutschein(g);
    } catch { setGutscheinFehler("Fehler beim Prüfen des Codes."); }
  };

  // Haversine Distanz in km
  const haversine = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2)**2 + Math.cos(lat1 * Math.PI/180) * Math.cos(lat2 * Math.PI/180) * Math.sin(dLon/2)**2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  };

  // Hof-Koordinaten (Ostlandweg 1, 38464 Groß Twülpstedt)
  const HOF_LAT = 52.4167;
  const HOF_LON = 10.8833;
  const MAX_KM  = 40;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!dsgvoAkzeptiert) {
      setError("Bitte akzeptiere die Datenschutzerklärung um fortzufahren.");
      return;
    }
    try {
      // Mindestmenge prüfen
      const produkt = produkte.find(p => String(p.produktId) === String(form.produktId));
      if (produkt && parseFloat(form.menge) < produkt.mindestMenge) {
        setError(`Mindestbestellmenge für ${produkt.name}: ${produkt.mindestMenge} ${produkt.einheit}`);
        return;
      }

      setError(null);

      const startDatum = form.wunschStart;

      // Ablageort in Notiz einbauen wenn gesetzt
      const notizKomplett = [form.ablageort ? `Ablageort: ${form.ablageort}` : "", form.notiz].filter(Boolean).join(" | ");

      const result = await api.abos.create({
        ...form,
        notiz: notizKomplett || null,
        startDatum,
        userId: user.userId,
        rechnungsAdresseId: form.rechnungsAdresseId ? parseInt(form.rechnungsAdresseId) : null,
      });
      setForm(FORM_LEER);

      // Für Firma-Einzelbestellungen wird die Rechnung im Backend angelegt
      if (!(user?.rolle === "Firma" && form.intervall === "Einzelbestellung")) {
        await api.payment.rechnungAbo({ bestellungId: result.bestellungId });
      }
      setSuccess(true);
    } catch (err) { setError(err.message || "Fehler beim Erstellen."); }
  };

  return (
    <div style={{ marginTop: "1.5rem" }}>
      <div style={{ display: "flex", gap: "0.75rem", marginBottom: "1.25rem", flexWrap: "wrap" }}>
        {[
          { key: "abo-neu",     label: "📦 Neues Abo" },
          { key: "abo-pause",   label: "⏸️ Pausieren" },
          { key: "abo-aendern", label: "✏️ Ändern" },
          { key: "abo-beenden", label: "❌ Beenden" },
        ].map(t => (
          <button key={t.key}
            className={`btn ${tab === t.key ? "btn-success" : "btn-secondary"}`}
            onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "abo-neu" && (
        <div className="card" style={{ border: "1.5px solid #a5d6a7" }}>
          <h3 style={{ color: "#2e7d32", marginBottom: "1rem" }}>📦 Neues Abo / Bestellung</h3>
          {success && <div className="alert alert-success">✓ Abo erstellt!</div>}
          {error   && <div className="alert alert-error">{error}</div>}
          <form onSubmit={handleSubmit}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 1rem" }}>
              <div className="form-group">
                <label>Produkt *</label>
                <select value={form.produktId} onChange={e => setForm({ ...form, produktId: e.target.value })} required>
                  <option value="">– wählen –</option>
                  {produkte
                    .filter(p => user?.rolle === "Firma" || ![
                      "1 Liter EG Weidemilch [EINWEG]",
                      "1 Liter EG Kakao [EINWEG]",
                      "0,25 Liter EG Weidemilch [Mehrweg]",
                    ].includes(p.name))
                    .map(p => (
                      <option key={p.produktId} value={p.produktId}>
                        {p.name} ({p.preis?.toFixed(2)} € / {p.einheit})
                      </option>
                    ))}
                </select>
                {form.produktId && (() => {
                  const p = produkte.find(x => String(x.produktId) === String(form.produktId));
                  return p?.mindestMenge > 1 ? (
                    <small style={{ color: "#e65100" }}>⚠️ Mindestmenge: {p.mindestMenge} {p.einheit}</small>
                  ) : null;
                })()}
              </div>
              <div className="form-group">
                <label>Menge *</label>
                <input type="number" min="1" value={form.menge}
                  onChange={e => setForm({ ...form, menge: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Intervall{user?.rolle === "Firma" ? " *" : ""}</label>
                {user?.rolle === "Firma" ? (
                  <select value={form.intervall} onChange={e => setForm({ ...form, intervall: e.target.value })}>
                    <option value="Einzelbestellung">Einzelbestellung</option>
                    <option value="Wöchentlich">Wöchentlich</option>
                    <option value="2-wöchentlich">2-wöchentlich</option>
                    <option value="Monatlich">Monatlich</option>
                  </select>
                ) : (
                  <input type="text" value="Wöchentlich" readOnly style={{ background: "#f5f5f5", color: "#555" }} />
                )}
              </div>
              <div className="form-group">
                <label>Ab wann? *</label>
                <input type="date" value={form.wunschStart} min={toLocalISO(new Date())}
                  onChange={e => setForm({ ...form, wunschStart: e.target.value })} required />
              </div>
            </div>
            <div className="form-group">
              <label>Ablageort *</label>
              <input value={form.ablageort} onChange={e => setForm({ ...form, ablageort: e.target.value })}
                placeholder="z.B. Vor der Haustür, Briefkasten, Garage..." required />
            </div>
            <div className="form-group">
              <label>Notiz (optional)</label>
              <textarea rows={2} value={form.notiz}
                onChange={e => setForm({ ...form, notiz: e.target.value })}
                placeholder="z.B. Bitte vor die Tür stellen..." />
            </div>
            <p style={{ fontWeight: 600, color: "#555", margin: "0.5rem 0" }}>📍 Lieferadresse</p>
            <div style={{ background: "#e3f2fd", border: "1px solid #90caf9", borderRadius: "8px", padding: "0.6rem 1rem", fontSize: "0.88rem", color: "#1565c0", marginBottom: "0.75rem" }}>
              🚚 Bitte haben Sie Verständnis, dass wir Kunden weit außerhalb unseres Gebietes nicht beliefern können.
            </div>
            {savedAdressen.filter(a => a.typ === "lieferung" || a.istStandard).length > 0 && (
              <div className="form-group">
                <label>Gespeicherte Adresse übernehmen</label>
                <select onChange={e => {
                  const a = savedAdressen.find(x => String(x.adresseId) === e.target.value);
                  if (a) setForm(f => ({ ...f, strasse: a.strasse ?? "", hausnummer: "", plz: a.plz ?? "", ort: a.ort ?? "" }));
                }} defaultValue="">
                  <option value="">– manuell eingeben –</option>
                  {savedAdressen.filter(a => a.typ === "lieferung" || a.istStandard).map(a => (
                    <option key={a.adresseId} value={a.adresseId}>{a.strasse}, {a.plz} {a.ort}</option>
                  ))}
                </select>
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 120px", gap: "0 1rem" }}>
              <div className="form-group">
                <label>Straße</label>
                <input value={form.strasse} onChange={e => setForm({ ...form, strasse: e.target.value })} placeholder="Musterstraße" />
              </div>
              <div className="form-group">
                <label>Nr.</label>
                <input value={form.hausnummer} onChange={e => setForm({ ...form, hausnummer: e.target.value })} placeholder="12a" />
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: "0 1rem" }}>
              <div className="form-group">
                <label>PLZ</label>
                <input value={form.plz} onChange={e => setForm({ ...form, plz: e.target.value })} placeholder="12345" />
              </div>
              <div className="form-group">
                <label>Ort</label>
                <input value={form.ort} onChange={e => setForm({ ...form, ort: e.target.value })} placeholder="Musterstadt" />
              </div>
            </div>
            {savedAdressen.filter(a => a.typ === "rechnung").length > 0 && (
              <div className="form-group">
                <p style={{ fontWeight: 600, color: "#555", margin: "0.75rem 0 0.25rem" }}>🧾 Rechnungsadresse (optional)</p>
                <select value={form.rechnungsAdresseId} onChange={e => setForm(f => ({ ...f, rechnungsAdresseId: e.target.value }))}>
                  <option value="">– wie Lieferadresse –</option>
                  {savedAdressen.filter(a => a.typ === "rechnung").map(a => (
                    <option key={a.adresseId} value={a.adresseId}>{a.strasse}, {a.plz} {a.ort}</option>
                  ))}
                </select>
              </div>
            )}
            {/* Gutschein */}
            <div className="form-group" style={{ marginTop: "0.5rem" }}>
              <label>🎟️ Gutscheincode (optional)</label>
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <input
                  value={gutscheinCode}
                  onChange={e => { setGutscheinCode(e.target.value); setGutschein(null); setGutscheinFehler(null); }}
                  placeholder="z.B. SOMMER24"
                  style={{ textTransform: "uppercase", flex: 1 }}
                />
                <button type="button" className="btn btn-secondary" onClick={pruefeGutschein}
                  style={{ whiteSpace: "nowrap" }}>
                  Einlösen
                </button>
              </div>
              {gutscheinFehler && <small style={{ color: "#c62828" }}>{gutscheinFehler}</small>}
              {gutschein && (
                <small style={{ color: "#2e7d32" }}>
                  ✓ Code <strong>{gutschein.code}</strong> – {gutschein.rabattTyp === "Prozent" ? `${gutschein.rabatt}% Rabatt` : `${gutschein.rabatt?.toFixed(2)} € Rabatt`}
                </small>
              )}
            </div>

            {/* Preisvorschau */}
            {(() => {
              const prod = produkte.find(p => String(p.produktId) === String(form.produktId));
              if (!prod) return null;
              const basis = prod.preis * form.menge;
              const lieferkosten = lieferpauschale;
              let rabatt = 0;
              if (gutschein) {
                rabatt = gutschein.rabattTyp === "Prozent"
                  ? basis * (gutschein.rabatt / 100)
                  : Math.min(gutschein.rabatt, basis);
              }
              const final = Math.max(0, basis + lieferkosten - rabatt);
              return (
                <div style={{ background: "#f1f8e9", border: "1px solid #a5d6a7", borderRadius: "8px", padding: "0.75rem 1rem", marginBottom: "0.75rem", fontSize: "0.92rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>Produktpreis</span><span>{basis.toFixed(2)} €</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", color: "#555" }}>
                    <span>Lieferpauschale</span><span>+ {lieferkosten.toFixed(2)} €</span>
                  </div>
                  {rabatt > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", color: "#2e7d32" }}>
                      <span>Rabatt ({gutschein.code})</span><span>− {rabatt.toFixed(2)} €</span>
                    </div>
                  )}
                  <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, borderTop: "1px solid #c8e6c9", marginTop: "0.4rem", paddingTop: "0.4rem" }}>
                    <span>Gesamt</span><span>{final.toFixed(2)} €</span>
                  </div>
                </div>
              );
            })()}

            <label style={{ display: "flex", alignItems: "flex-start", gap: "0.6rem", marginBottom: "1rem", cursor: "pointer", fontSize: "0.88rem", lineHeight: 1.5 }}>
              <input
                type="checkbox"
                checked={dsgvoAkzeptiert}
                onChange={e => setDsgvoAkzeptiert(e.target.checked)}
                style={{ marginTop: "0.15rem", flexShrink: 0, width: 16, height: 16, cursor: "pointer" }}
              />
              <span style={{ color: "#555" }}>
                Ich habe die{" "}
                <a href="/datenschutz" target="_blank" rel="noreferrer" style={{ color: "#2e7d32", fontWeight: 600 }}>
                  Datenschutzerklärung
                </a>{" "}
                gelesen und stimme der Verarbeitung meiner personenbezogenen Daten zur Abwicklung meiner Bestellung gemäß DSGVO zu. *
              </span>
            </label>

            <button className="btn btn-success" type="submit" disabled={!dsgvoAkzeptiert}>✓ Abo erstellen</button>
          </form>
        </div>
      )}

      {tab === "abo-pause" && (
        <PausierenForm abos={abos} betriebsurlaub={betriebsurlaub} />
      )}

      {tab === "abo-aendern" && (
        <AendernForm abos={abos} produkte={produkte} />
      )}

      {tab === "abo-beenden" && (
        <BeendenForm abos={abos} onBeendet={() => api.abos.getByUser(user.userId)} />
      )}
    </div>
  );
}

// ── Einzelbestellung ──────────────────────────────────────
function EinzelbestellungForm({ user, produkte }) {
  const leer = { produktId: "", menge: 1, lieferart: "lieferung", strasse: "", hausnummer: "", plz: "", ort: "", notiz: "", ablageort: "", rechnungsAdresseId: "" };
  const [form,    setForm]    = useState(leer);
  const [savedAdressen, setSavedAdressen] = useState([]);

  useEffect(() => {
    if (user) api.adressen.getByUser(user.userId).then(setSavedAdressen).catch(() => {});
  }, [user]);
  const [error,   setError]   = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [einzelGutscheinCode, setEinzelGutscheinCode] = useState("");
  const [einzelGutschein,     setEinzelGutschein]     = useState(null);
  const [einzelGutscheinFehler, setEinzelGutscheinFehler] = useState(null);
  const [lieferpauschale, setLieferpauschale] = useState(1.60);

  useEffect(() => {
    api.einstellungen.get("Lieferpauschale")
      .then(v => setLieferpauschale(parseFloat(v)))
      .catch(() => {});
  }, []);

  const pruefeEinzelGutschein = async () => {
    setEinzelGutscheinFehler(null);
    setEinzelGutschein(null);
    if (!einzelGutscheinCode.trim()) return;
    try {
      const g = await api.gutscheine.pruefen(einzelGutscheinCode.trim()).catch(() => null);
      if (!g?.istAktiv) { setEinzelGutscheinFehler("❌ Ungültiger oder inaktiver Code."); return; }
      if (g.gueltigBis && new Date(g.gueltigBis + "T23:59:59") < new Date()) { setEinzelGutscheinFehler("❌ Code ist abgelaufen."); return; }
      if (g.maxNutzungen && g.nutzungen >= g.maxNutzungen) { setEinzelGutscheinFehler("❌ Code wurde bereits zu oft verwendet."); return; }
      setEinzelGutschein(g);
    } catch { setEinzelGutscheinFehler("Fehler beim Prüfen des Codes."); }
  };

  const GutscheinFeld = () => (
    <div className="form-group" style={{ marginTop: "0.5rem" }}>
      <label>🎟️ Gutscheincode (optional)</label>
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <input
          value={einzelGutscheinCode}
          onChange={e => { setEinzelGutscheinCode(e.target.value); setEinzelGutschein(null); setEinzelGutscheinFehler(null); }}
          placeholder="z.B. SOMMER24"
          style={{ textTransform: "uppercase", flex: 1 }}
        />
        <button type="button" className="btn btn-secondary" onClick={pruefeEinzelGutschein}
          style={{ whiteSpace: "nowrap" }}>
          Einlösen
        </button>
      </div>
      {einzelGutscheinFehler && <small style={{ color: "#c62828" }}>{einzelGutscheinFehler}</small>}
      {einzelGutschein && (
        <small style={{ color: "#2e7d32" }}>
          ✓ Code <strong>{einzelGutschein.code}</strong> – {einzelGutschein.rabattTyp === "Prozent" ? `${einzelGutschein.rabatt}% Rabatt` : `${einzelGutschein.rabatt?.toFixed(2)} € Rabatt`}
        </small>
      )}
    </div>
  );

  const gewProdukt = produkte.find(p => String(p.produktId) === String(form.produktId));
  const istAbholung = form.lieferart === "abholung";
  const aktiverPreis = istAbholung && gewProdukt?.abholPreis != null
    ? gewProdukt.abholPreis
    : gewProdukt?.preis;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const produkt = produkte.find(p => String(p.produktId) === String(form.produktId));
    if (produkt && parseFloat(form.menge) < produkt.mindestMenge) {
      setError(`Mindestbestellmenge: ${produkt.mindestMenge} ${produkt.einheit}`);
      return;
    }

    // Adresse nur bei Lieferung prüfen
    if (!istAbholung && form.strasse && form.plz && form.ort) {
      setLoading(true);
      const adresse = encodeURIComponent(`${form.strasse} ${form.hausnummer}, ${form.plz} ${form.ort}, Deutschland`);
      const res  = await fetch(`https://nominatim.openstreetmap.org/search?q=${adresse}&format=json&limit=1`);
      const data = await res.json();
      if (data.length === 0) { setError("Adresse nicht gefunden."); setLoading(false); return; }
      const R = 6371;
      const dLat = (parseFloat(data[0].lat) - 52.4089) * Math.PI / 180;
      const dLon = (parseFloat(data[0].lon) - 10.8601) * Math.PI / 180;
      const a = Math.sin(dLat/2)**2 + Math.cos(52.4089 * Math.PI/180) * Math.cos(parseFloat(data[0].lat) * Math.PI/180) * Math.sin(dLon/2)**2;
      const km = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      if (km > 40) { setError(`❌ Deine Adresse liegt ${km.toFixed(1)} km vom Hof entfernt und befindet sich leider außerhalb unseres Liefergebiets (max. 40 km um Groß Twülpstedt). Bitte wende dich bei Fragen an uns.`); setLoading(false); return; }
    }

    setLoading(true);
    try {
      const heute = toLocalISO(new Date());
      const notizKomplett = [
        istAbholung ? "Abholung" : (form.ablageort ? `Ablageort: ${form.ablageort}` : ""),
        form.notiz
      ].filter(Boolean).join(" | ");
      const result = await api.abos.create({
        ...form,
        strasse:            istAbholung ? null : form.strasse,
        hausnummer:         istAbholung ? null : form.hausnummer,
        plz:                istAbholung ? null : form.plz,
        ort:                istAbholung ? null : form.ort,
        intervall:          "Einzelbestellung",
        startDatum:         heute,
        notiz:              notizKomplett || null,
        userId:             user.userId,
        rechnungsAdresseId: form.rechnungsAdresseId ? parseInt(form.rechnungsAdresseId) : null,
      });

      setForm(leer);
      await api.payment.rechnungAbo({ bestellungId: result.bestellungId });
      setSuccess(true);
    } catch { setError("Fehler beim Bestellen."); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ marginTop: "1.5rem" }}>
      <div className="card" style={{ border: "1.5px solid #a5d6a7" }}>
        <h3 style={{ color: "#2e7d32", marginBottom: "1rem" }}>🛒 Einzelbestellung</h3>
        <p style={{ color: "#666", fontSize: "0.9rem", marginBottom: "1.25rem" }}>
          Einmalige Bestellung ohne Abo – direkt bestellen und bezahlen.
        </p>
        {success && <div className="alert alert-success">✓ Bestellung erfolgreich aufgegeben.</div>}
        {error && <div className="alert alert-error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 1rem" }}>
            <div className="form-group">
              <label>Produkt *</label>
              <select value={form.produktId} onChange={e => {
                setForm({ ...form, produktId: e.target.value });
              }} required>
                <option value="">– wählen –</option>
                {produkte.map(p => (
                  <option key={p.produktId} value={p.produktId}>
                    {p.name} ({p.preis?.toFixed(2)} € / {p.einheit})
                  </option>
                ))}
              </select>
              {form.produktId && (() => {
                const p = produkte.find(x => String(x.produktId) === String(form.produktId));
                return p?.mindestMenge > 1 ? <small style={{ color: "#e65100" }}>⚠️ Mindestmenge: {p.mindestMenge} {p.einheit}</small> : null;
              })()}
            </div>
            <div className="form-group">
              <label>Menge *</label>
              <input type="number" min="1" value={form.menge}
                onChange={e => setForm({ ...form, menge: e.target.value })} required />
            </div>
          </div>

          {/* Lieferart Toggle */}
          <div style={{ display: "flex", gap: "0.75rem", marginBottom: "1.25rem" }}>
            {[
              { val: "lieferung", icon: "🚚", label: "Lieferung" },
              { val: "abholung",  icon: "🏠", label: "Selbst abholen" },
            ].map(o => (
              <button key={o.val} type="button"
                onClick={() => setForm({ ...form, lieferart: o.val })}
                style={{
                  flex: 1, padding: "0.65rem", borderRadius: "10px", cursor: "pointer",
                  border: `2px solid ${form.lieferart === o.val ? "#2e7d32" : "#e0e0e0"}`,
                  background: form.lieferart === o.val ? "#f1f8e9" : "white",
                  fontWeight: 600, color: form.lieferart === o.val ? "#2e7d32" : "#555",
                }}>
                {o.icon} {o.label}
                {o.val === "abholung" && gewProdukt?.abholPreis != null && (
                  <span style={{ display: "block", fontSize: "0.75rem", fontWeight: 400 }}>
                    {gewProdukt.abholPreis.toFixed(2)} €/{gewProdukt.einheit}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Abholung Info */}
          {istAbholung && (
            <div style={{ background: "#e8f5e9", border: "1px solid #a5d6a7", borderRadius: "8px", padding: "0.65rem 1rem", marginBottom: "1rem", fontSize: "0.9rem", color: "#2e7d32" }}>
              🏠 <strong>Abholung:</strong> Ostlandweg 1, 38464 Groß Twülpstedt · Mo–Fr 8–17 Uhr
            </div>
          )}

          {/* Lieferadresse (nur bei Lieferung) */}
          {!istAbholung && (<>
            <p style={{ fontWeight: 600, color: "#555", margin: "0.5rem 0" }}>📍 Lieferadresse</p>
            <div style={{ background: "#e3f2fd", border: "1px solid #90caf9", borderRadius: "8px", padding: "0.6rem 1rem", fontSize: "0.88rem", color: "#1565c0", marginBottom: "0.75rem" }}>
              🚚 Lieferung nur im Umkreis von <strong>40 km</strong> um Groß Twülpstedt.
            </div>
            {savedAdressen.filter(a => a.typ === "lieferung" || a.istStandard).length > 0 && (
              <div className="form-group">
                <label>Gespeicherte Adresse übernehmen</label>
                <select onChange={e => {
                  const a = savedAdressen.find(x => String(x.adresseId) === e.target.value);
                  if (a) setForm(f => ({ ...f, strasse: a.strasse ?? "", hausnummer: "", plz: a.plz ?? "", ort: a.ort ?? "" }));
                  else setForm(f => ({ ...f, strasse: "", hausnummer: "", plz: "", ort: "" }));
                }} defaultValue="">
                  <option value="">– manuell eingeben –</option>
                  {savedAdressen.filter(a => a.typ === "lieferung" || a.istStandard).map(a => (
                    <option key={a.adresseId} value={a.adresseId}>{a.strasse}, {a.plz} {a.ort}</option>
                  ))}
                </select>
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 120px", gap: "0 1rem" }}>
              <div className="form-group"><label>Straße</label><input value={form.strasse} onChange={e => setForm({ ...form, strasse: e.target.value })} placeholder="Musterstraße" /></div>
              <div className="form-group"><label>Nr.</label><input value={form.hausnummer} onChange={e => setForm({ ...form, hausnummer: e.target.value })} placeholder="12a" /></div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: "0 1rem" }}>
              <div className="form-group"><label>PLZ</label><input value={form.plz} onChange={e => setForm({ ...form, plz: e.target.value })} placeholder="12345" /></div>
              <div className="form-group"><label>Ort</label><input value={form.ort} onChange={e => setForm({ ...form, ort: e.target.value })} placeholder="Musterstadt" /></div>
            </div>
            {savedAdressen.filter(a => a.typ === "rechnung").length > 0 && (
              <div className="form-group">
                <label>🧾 Rechnungsadresse (optional)</label>
                <select value={form.rechnungsAdresseId} onChange={e => setForm(f => ({ ...f, rechnungsAdresseId: e.target.value }))}>
                  <option value="">– wie Lieferadresse –</option>
                  {savedAdressen.filter(a => a.typ === "rechnung").map(a => (
                    <option key={a.adresseId} value={a.adresseId}>{a.strasse}, {a.plz} {a.ort}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="form-group">
              <label>Ablageort *</label>
              <input value={form.ablageort} onChange={e => setForm({ ...form, ablageort: e.target.value })}
                placeholder="z.B. Vor der Haustür, Briefkasten..." required={!istAbholung} />
            </div>
          </>)}

          <div className="form-group">
            <label>Notiz (optional)</label>
            <textarea rows={2} value={form.notiz} onChange={e => setForm({ ...form, notiz: e.target.value })}
              placeholder="z.B. Bitte nicht klingeln..." />
          </div>

          {/* Gutschein */}
          <GutscheinFeld />

          {/* Preisvorschau */}
          {gewProdukt && (() => {
            const basis = aktiverPreis * form.menge;
            const lieferkosten = istAbholung ? 0 : lieferpauschale;
            let rabatt = 0;
            if (einzelGutschein) {
              rabatt = einzelGutschein.rabattTyp === "Prozent"
                ? basis * (einzelGutschein.rabatt / 100)
                : Math.min(einzelGutschein.rabatt, basis);
            }
            const final = Math.max(0, basis + lieferkosten - rabatt);
            return (
              <div style={{ background: "#f1f8e9", border: "1px solid #a5d6a7", borderRadius: "8px", padding: "0.75rem 1rem", marginBottom: "0.75rem", fontSize: "0.92rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>Produktpreis</span><span>{basis.toFixed(2)} €</span>
                </div>
                {!istAbholung && (
                  <div style={{ display: "flex", justifyContent: "space-between", color: "#555" }}>
                    <span>Lieferpauschale</span><span>+ {lieferkosten.toFixed(2)} €</span>
                  </div>
                )}
                {rabatt > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between", color: "#2e7d32" }}>
                    <span>Rabatt ({einzelGutschein.code})</span><span>− {rabatt.toFixed(2)} €</span>
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, borderTop: "1px solid #c8e6c9", marginTop: "0.4rem", paddingTop: "0.4rem" }}>
                  <span>Gesamt</span><span>{final.toFixed(2)} €</span>
                </div>
              </div>
            );
          })()}

          <button className="btn btn-success" type="submit" disabled={loading || !form.produktId}>
            {loading ? "⏳ Wird geprüft..." : "🛒 Jetzt bestellen & bezahlen"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── SEPA-Lastschrift ──────────────────────────────────────
function SepaSection({ userId }) {
  const [form, setForm]       = useState({ sepaIban: "", sepaMandatRef: "", sepaMandatDatum: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError]     = useState(null);

  useEffect(() => {
    api.kunden.getSepa(userId)
      .then(d => setForm({
        sepaIban:        d?.sepaIban        ?? "",
        sepaMandatRef:   d?.sepaMandatRef   ?? "",
        sepaMandatDatum: d?.sepaMandatDatum ?? "",
      }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setError(null); setSuccess(false);
    try {
      await api.kunden.updateSepa(userId, {
        sepaIban:        form.sepaIban       || null,
        sepaMandatRef:   form.sepaMandatRef  || null,
        sepaMandatDatum: form.sepaMandatDatum || null,
      });
      setSuccess(true);
    } catch (err) { setError(err.message || "Fehler beim Speichern."); }
    finally { setSaving(false); }
  };

  if (loading) return <div style={{ marginTop: "1.5rem", color: "#aaa" }}>Lädt...</div>;

  return (
    <div style={{ marginTop: "1.5rem", maxWidth: 500 }}>
      <div className="card" style={{ border: "1.5px solid #bbdefb" }}>
        <h3 style={{ color: "#1565c0", marginBottom: "0.5rem" }}>🏦 SEPA-Lastschrift</h3>
        <p style={{ color: "#666", fontSize: "0.9rem", marginBottom: "1.25rem" }}>
          Hinterlege deine Bankdaten, um die Zahlung per SEPA-Lastschrift zu ermöglichen.
          Deine IBAN wird sicher gespeichert und nur für Abbuchungen im Rahmen deiner Bestellungen verwendet.
        </p>
        {success && <div className="alert alert-success">✓ SEPA-Daten erfolgreich gespeichert!</div>}
        {error   && <div className="alert alert-error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>IBAN</label>
            <input type="text" placeholder="DE12 3456 7890 1234 5678 90"
              value={form.sepaIban}
              onChange={e => setForm({ ...form, sepaIban: e.target.value })}
              style={{ fontFamily: "monospace" }} />
          </div>
          <div className="form-group">
            <label>Mandatsreferenz <small style={{ color: "#aaa" }}>(vom Hof vergeben)</small></label>
            <input type="text" placeholder="MAND-001"
              value={form.sepaMandatRef}
              onChange={e => setForm({ ...form, sepaMandatRef: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Mandatsdatum</label>
            <input type="date"
              value={form.sepaMandatDatum}
              onChange={e => setForm({ ...form, sepaMandatDatum: e.target.value })} />
          </div>
          <button className="btn" type="submit" disabled={saving}
            style={{ background: "#1565c0", color: "white", width: "100%" }}>
            {saving ? "Wird gespeichert..." : "💾 SEPA-Daten speichern"}
          </button>
        </form>
        {form.sepaIban && (
          <div style={{ marginTop: "1rem", background: "#e8f5e9", borderRadius: "8px", padding: "0.75rem 1rem", fontSize: "0.88rem", color: "#2e7d32" }}>
            ✓ SEPA-Lastschrift aktiv – Zahlungen werden automatisch eingezogen.
          </div>
        )}
      </div>
    </div>
  );
}

// ── Hauptseite ────────────────────────────────────────────
export default function Profil() {
  const { user } = useAuth();
  const [produkte, setProdukte]               = useState([]);
  const [besichtigungen, setBesichtigungen]   = useState([]);
  const [meineBesichtigungen, setMeineBesichtigungen] = useState([]);
  const [abos, setAbos]                       = useState([]);
  const [betriebsurlaub, setBetriebsurlaub]   = useState([]);
  const [activeTab, setActiveTab]             = useState("abo");
  const [flaschen, setFlaschen]               = useState(0);

  const loadBesichtigungen = () => {
    api.besichtigungen.getAll().then(setBesichtigungen).catch(() => {});
    if (user) api.besichtigungen.getByUser(user.userId).then(setMeineBesichtigungen).catch(() => {});
  };

  useEffect(() => {
    api.produkte.getAll().then(setProdukte).catch(() => {});
    api.betriebsurlaub.getAll().then(setBetriebsurlaub).catch(() => {});
    loadBesichtigungen();
    if (user) {
      api.abos.getByUser(user.userId).then(setAbos).catch(() => {});
      api.flaschen.getByUser(user.userId).then(r => setFlaschen(r.anzahl)).catch(() => {});
    }
  }, [user]);

  if (!user) {
    return (
      <div className="container" style={{ maxWidth: 600, textAlign: "center", paddingTop: "3rem" }}>
        <div className="card">
          <h2 style={{ color: "#2e7d32" }}>🔒 Nicht eingeloggt</h2>
          <p style={{ marginTop: "0.75rem", color: "#555" }}>Bitte melde dich an um dein Profil zu sehen.</p>
          <Link to="/" className="btn btn-success" style={{ marginTop: "1rem", display: "inline-block" }}>
            Zur Startseite
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ maxWidth: 900 }}>
      <h2 className="section-title" style={{ marginTop: "1rem" }}>Mein Profil</h2>

      {/* ── Profil Card ── */}
      <div className="card" style={{ marginTop: "1.5rem", border: "1.5px solid #a5d6a7" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "1.5rem" }}>
          <div style={{
            width: 60, height: 60, borderRadius: "50%",
            background: "linear-gradient(135deg, #2e7d32, #66bb6a)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "1.5rem", color: "white", fontWeight: 700, flexShrink: 0
          }}>
            {user.vorname?.[0]}{user.nachname?.[0]}
          </div>
          <div>
            <h3 style={{ color: "#1b5e20" }}>{user.vorname} {user.nachname}</h3>
            <p style={{ color: "#555", fontSize: "0.9rem" }}>{user.email}</p>
            {user.isAdmin && (
              <span className="badge" style={{ background: "#fff3e0", color: "#e65100" }}>👑 Administrator</span>
            )}
          </div>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div style={{ display: "flex", gap: "0.75rem", margin: "2rem 0 0", flexWrap: "wrap" }}>
        <button className={`btn ${activeTab === "abo" ? "btn-success" : "btn-secondary"}`}
          onClick={() => setActiveTab("abo")}>📦 Meine Abos</button>
        <button className={`btn ${activeTab === "kalender" ? "btn-success" : "btn-secondary"}`}
          onClick={() => setActiveTab("kalender")}>📅 Lieferkalender</button>
        {/* Einzelbestellungen nur für Firmen – Privatkunden bestellen im Abo, Einmaliges legt der Admin an */}
        {user.rolle === "Firma" && (
          <button className={`btn ${activeTab === "einzelbestellung" ? "btn-success" : "btn-secondary"}`}
            onClick={() => setActiveTab("einzelbestellung")}>🛒 Einzelbestellung</button>
        )}
        <button className={`btn ${activeTab === "flaschen" ? "btn-success" : "btn-secondary"}`}
          onClick={() => setActiveTab("flaschen")}>🍼 Meine Flaschen</button>
        <button className={`btn ${activeTab === "besichtigung-anfrage" ? "btn-success" : "btn-secondary"}`}
          onClick={() => setActiveTab("besichtigung-anfrage")}>🐄 Besichtigung anfragen</button>
        <button className={`btn ${activeTab === "meine-besichtigungen" ? "btn-success" : "btn-secondary"}`}
          onClick={() => setActiveTab("meine-besichtigungen")}>📅 Meine Besichtigungen</button>
        <button className={`btn ${activeTab === "sepa" ? "btn-success" : "btn-secondary"}`}
          onClick={() => setActiveTab("sepa")}>🏦 SEPA-Lastschrift</button>
        <button className={`btn ${activeTab === "passwort" ? "btn-success" : "btn-secondary"}`}
          onClick={() => setActiveTab("passwort")}>🔒 Passwort ändern</button>
        <button className={`btn ${activeTab === "nachricht" ? "btn-success" : "btn-secondary"}`}
          onClick={() => setActiveTab("nachricht")}>✉️ Nachricht schreiben</button>
      </div>

      {activeTab === "abo" && (
        <>
          {/* ── Abo Übersicht ── */}
          {abos.filter(a => a.istAktiv && a.intervall !== "Einzelbestellung").length > 0 && (
            <div style={{ marginTop: "1.5rem", marginBottom: "0.5rem" }}>
              <h3 style={{ color: "#2e7d32", marginBottom: "0.75rem" }}>📦 Meine aktiven Abos</h3>
              <div className="card-grid">
                {abos.filter(a => a.istAktiv && a.intervall !== "Einzelbestellung").map(a => {
                  const heute = new Date();
                  heute.setHours(0, 0, 0, 0);

                  // Hilfsfunktion: liegt ein Datum in einem Betriebsurlaub?
                  const inUrlaub = (d) => betriebsurlaub.some(u => {
                    const von = new Date(u.urlaubVon + "T00:00:00");
                    const bis = new Date(u.urlaubBis + "T00:00:00");
                    return d >= von && d <= bis;
                  });

                  // Tour-Wochentage: DB 1=Mo…7=So → JS getDay() 0=So,1=Mo…6=Sa
                  const tourTage = a.tourWochentage
                    ? a.tourWochentage.split(",").map(n => parseInt(n) % 7)
                    : [1]; // Fallback: Montag

                  // Nächsten Tour-Tag ab heute finden, Betriebsurlaub überspringen
                  let naechste = null;
                  let d = new Date(heute);
                  let sicherheit = 0;
                  while (sicherheit++ < 365) {
                    if (tourTage.includes(d.getDay()) && !inUrlaub(d)) {
                      naechste = d;
                      break;
                    }
                    d = new Date(d);
                    d.setDate(d.getDate() + 1);
                  }

                  const istPausiert = a.pauseVon && new Date(a.pauseVon + "T00:00:00") <= heute &&
                                      a.pauseBis && new Date(a.pauseBis + "T00:00:00") >= heute;

                  return (
                    <div className="card" key={a.aboId} style={{ border: "1.5px solid #a5d6a7", position: "relative" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <div>
                          <strong style={{ fontSize: "1.05rem", color: "#1b5e20" }}>{a.produktName}</strong>
                          <br /><small style={{ color: "#888" }}>{a.menge} {a.einheit} · {a.intervall}</small>
                        </div>
                        {istPausiert
                          ? <span className="badge" style={{ background: "#fff3e0", color: "#e65100" }}>⏸️ Pausiert</span>
                          : <span className="badge" style={{ background: "#e8f5e9", color: "#2e7d32" }}>✓ Aktiv</span>
                        }
                      </div>
                      {naechste && !istPausiert && (
                        <div style={{ marginTop: "0.75rem", background: "#f1f8e9", borderRadius: "8px", padding: "0.5rem 0.75rem", fontSize: "0.9rem" }}>
                          🚚 Nächste Lieferung: <strong>{naechste.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long" })}</strong>
                        </div>
                      )}
                      {istPausiert && a.pauseBis && (
                        <div style={{ marginTop: "0.75rem", background: "#fff3e0", borderRadius: "8px", padding: "0.5rem 0.75rem", fontSize: "0.9rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span>⏸️ Pausiert bis: <strong>{new Date(a.pauseBis + "T00:00:00").toLocaleDateString("de-DE", { day: "2-digit", month: "long" })}</strong></span>
                          <button
                            onClick={async () => {
                              if (!window.confirm("Pause jetzt vorzeitig aufheben?")) return;
                              await api.abos.pauseAufheben(a.aboId);
                              const fresh = await api.abos.getByUser(user.userId);
                              setAbos(fresh);
                            }}
                            style={{ padding: "0.25rem 0.65rem", fontSize: "0.8rem", background: "#e8f5e9", color: "#2e7d32", border: "1px solid #a5d6a7", borderRadius: "6px", cursor: "pointer", whiteSpace: "nowrap" }}>
                            ▶ Fortsetzen
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <AboForms user={user} produkte={produkte} abos={abos} betriebsurlaub={betriebsurlaub} />
        </>
      )}

      {activeTab === "einzelbestellung" && (
        <div style={{ marginTop: "1.5rem" }}>
          <EinzelbestellungForm user={user} produkte={produkte} />
        </div>
      )}

      {activeTab === "flaschen" && (
        <div className="card" style={{ marginTop: "1.5rem", border: "1.5px solid #a5d6a7", maxWidth: 500 }}>
          <h3 style={{ color: "#2e7d32", marginBottom: "1.25rem" }}>🍼 Meine Pfandflaschen</h3>

          {/* Große Zahl */}
          <div style={{ textAlign: "center", padding: "1.5rem 0", background: "#f1f8e9", borderRadius: "10px", marginBottom: "1.25rem" }}>
            <div style={{ fontSize: "3.5rem", fontWeight: 700, color: "#2e7d32", lineHeight: 1 }}>{flaschen}</div>
            <div style={{ color: "#555", marginTop: "0.4rem", fontSize: "0.95rem" }}>Flaschen bei Ihnen</div>
          </div>

          {/* Info */}
          <div style={{ background: "#e3f2fd", border: "1px solid #90caf9", borderRadius: "8px", padding: "0.75rem 1rem", fontSize: "0.9rem", color: "#1565c0", marginBottom: "1rem" }}>
            ℹ️ Bitte geben Sie Ihre Pfandflaschen bei der nächsten Lieferung an den Fahrer zurück.
          </div>

          {flaschen > 0 && (
            <div style={{ background: "#fff3e0", border: "1px solid #ffcc80", borderRadius: "8px", padding: "0.75rem 1rem", fontSize: "0.9rem", color: "#e65100" }}>
              🔄 Sie sollten aktuell <strong>{flaschen} {flaschen === 1 ? "Flasche" : "Flaschen"}</strong> zurückgeben.
            </div>
          )}
          {flaschen === 0 && (
            <div style={{ background: "#e8f5e9", border: "1px solid #a5d6a7", borderRadius: "8px", padding: "0.75rem 1rem", fontSize: "0.9rem", color: "#2e7d32" }}>
              ✓ Sie haben aktuell keine ausstehenden Flaschen. Danke!
            </div>
          )}
        </div>
      )}

      {activeTab === "besichtigung-anfrage" && (
        <BesichtigungForm
          user={user}
          besichtigungen={besichtigungen}
          betriebsurlaub={betriebsurlaub}
          abos={abos}
          onSuccess={loadBesichtigungen}
        />
      )}

      {activeTab === "meine-besichtigungen" && (
        <MeineBesichtigungen besichtigungen={meineBesichtigungen} />
      )}

      {activeTab === "sepa" && (
        <SepaSection userId={user.userId} />
      )}

      {activeTab === "passwort" && (
        <PasswortAendern user={user} />
      )}

      {activeTab === "kalender" && (
        <Lieferkalender user={user} abos={abos} betriebsurlaub={betriebsurlaub} />
      )}

      {activeTab === "nachricht" && (
        <NachrichtSchreiben user={user} />
      )}
    </div>
  );
}
